import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import {
  getTagConfig,
  getAllTags,
  setTagConfigEnabled,
  createTag,
  updateTag,
  deleteTag,
} from '../../../actions/tagManagerActions';
import Header from '../../../components/Header/Header';
import Spinner from '../../../components/Spinner/Spinner';
import Confirm from '../../../components/Confirm/Confirm';
import TagManagerTable from '../../../components/TagManager/TagManagerTable';
import RenameTagDialog from '../../../components/TagManager/RenameTagDialog';
import TagManagerActions from './TagManagerActions';

/**
 * @typedef {import('./types').TagRow} TagRow
 * @typedef {import('./types').TagManagerState} TagManagerState
 * @typedef {import('./types').TagManagerPageProps} TagManagerPageProps
 */

/**
 * The local UI state of the Tag Manager page.
 *
 * @typedef {Object} TagManagerPageState
 * @property {string} newTag - The new tag input value.
 * @property {TagRow|null} renameRow - The row being renamed, or null.
 * @property {TagRow|null} deleteRow - The row pending deletion, or null.
 * @property {string} filter - The tag filter text.
 * @property {boolean} hideArchived - Whether archived rows are hidden.
 */

export class TagManagerPage extends Component {
  /**
   * @type {TagManagerPageProps}
   */
  props;

  /** @type {TagManagerPageState} */
  state = {
    newTag: '',
    renameRow: null,
    deleteRow: null,
    filter: '',
    hideArchived: true,
  };

  componentDidMount() {
    this.loadData();
    document.addEventListener('visibilitychange', this.loadData);
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.loadData);
  }

  /**
   * Loads the tag list and feature flag when the page is visible.
   *
   * @returns {void}
   */
  loadData = async () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    const { getAllTags, getTagConfig } = this.props;

    await Promise.all([getAllTags(), getTagConfig()]);
  };

  /**
   * Updates the new tag input value.
   *
   * @param {string} newTag - The new input value.
   * @returns {void}
   */
  onNewTagChange = newTag => {
    this.setState({ newTag });
  };

  /**
   * Toggles the Tag Manager feature flag.
   *
   * @returns {void}
   */
  onClickToggleEnabled = async () => {
    const {
      tagManager: { enabled },
      setTagConfigEnabled,
    } = this.props;

    await setTagConfigEnabled(!enabled);
  };

  /**
   * Creates a tag from the new tag input when it is non-empty.
   *
   * @returns {void}
   */
  onAddTag = () => {
    const { createTag } = this.props;
    const { newTag } = this.state;
    const tag = newTag.trim();

    if (!tag) {
      return;
    }

    this.setState({ newTag: '' }, () => createTag(tag));
  };

  /**
   * Opens the rename dialog for the given row.
   *
   * @param {TagRow} row - The tag row to rename.
   * @returns {void}
   */
  onClickRename = row => {
    this.setState({ renameRow: row });
  };

  /**
   * Submits the rename for the pending row.
   *
   * @param {string} newTag - The new tag label.
   * @returns {void}
   */
  onRenameSubmit = newTag => {
    const { updateTag } = this.props;
    const { renameRow } = this.state;

    this.setState({ renameRow: null }, () =>
      updateTag(renameRow.id, renameRow.tag, { tag: newTag }),
    );
  };

  /**
   * Closes the rename dialog without saving.
   *
   * @returns {void}
   */
  onRenameHide = () => {
    this.setState({ renameRow: null });
  };

  /**
   * Toggles the archived flag of the given row.
   *
   * @param {TagRow} row - The tag row to toggle.
   * @returns {void}
   */
  onVisibleToggle = async row => {
    const { updateTag } = this.props;

    await updateTag(row.id, row.tag, { archived: row.archived === 1 ? 0 : 1 });
  };

  /**
   * Opens the delete confirmation for the given row.
   *
   * @param {TagRow} row - The tag row to delete.
   * @returns {void}
   */
  onClickDelete = row => {
    this.setState({ deleteRow: row });
    this.refDelete.show('Delete Tag', `Are you sure you want to delete the tag "${row.tag}"?`);
    this.refDelete.onSuccess(this.onDeleteConfirm);
  };

  /**
   * Deletes the pending row after confirmation.
   *
   * @returns {void}
   */
  onDeleteConfirm = () => {
    const { deleteTag } = this.props;
    const { deleteRow } = this.state;

    this.setState({ deleteRow: null }, () => deleteTag(deleteRow.id, deleteRow.tag));
  };

  /**
   * Updates the tag filter text.
   *
   * @param {string} filter - The new filter text.
   * @returns {void}
   */
  onFilterChange = filter => {
    this.setState({ filter });
  };

  /**
   * Toggles the hide-archived filter.
   *
   * @returns {void}
   */
  onHideArchivedChange = () => {
    this.setState(prevState => ({ hideArchived: !prevState.hideArchived }));
  };

  /**
   * Renders the Tag Manager page.
   *
   * @returns {JSX.Element} The page markup.
   */
  render() {
    const {
      tagManager: {
        allTags,
        enabled,
        allTagsStatus,
        configStatus,
        allTagsError,
        createStatus,
        updateStatus,
        deleteStatus,
      },
    } = this.props;
    const { newTag, renameRow, filter, hideArchived } = this.state;
    const loading = allTagsStatus === 'idle' || allTagsStatus === 'loading';
    const mutationsPending =
      createStatus === 'loading' || updateStatus === 'loading' || deleteStatus === 'loading';
    const sortedAllTags = (allTags || [])
      .slice()
      .sort((a, b) => a.tag.toLowerCase().localeCompare(b.tag.toLowerCase()));

    if (allTagsError) {
      return (
        <div className="page-structure !bg-star-light min-h-screen">
          <Helmet title="Tag Manager" />
          <Header />
          <TagManagerActions
            enabled={enabled}
            onToggleEnabled={this.onClickToggleEnabled}
            configStatus={configStatus}
            newTag={newTag}
            onNewTagChange={this.onNewTagChange}
            onAddTag={this.onAddTag}
            addDisabled={mutationsPending}
          />
          <div className="mt-8 !bg-star-light">
            <div className="page-content page-tagmanager">
              <p>Unable to load tags. Please try again.</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="page-structure !bg-star-light min-h-screen">
        <Helmet title="Tag Manager" />
        <Header />
        <TagManagerActions
          enabled={enabled}
          onToggleEnabled={this.onClickToggleEnabled}
          configStatus={configStatus}
          newTag={newTag}
          onNewTagChange={this.onNewTagChange}
          onAddTag={this.onAddTag}
          addDisabled={mutationsPending}
        />
        <div className="mt-8 !bg-star-light">
          {enabled && (
            <div className="page-content page-tagmanager">
              <Spinner loading={loading}>
                <TagManagerTable
                  data={sortedAllTags}
                  actionsDisabled={mutationsPending}
                  onRename={this.onClickRename}
                  onArchiveToggle={this.onVisibleToggle}
                  onDelete={this.onClickDelete}
                  onFilterChange={this.onFilterChange}
                  onHideArchivedChange={this.onHideArchivedChange}
                  filterState={{ filter, hideArchived }}
                />
              </Spinner>
            </div>
          )}
        </div>
        <RenameTagDialog
          currentTag={renameRow ? renameRow.tag : ''}
          show={Boolean(renameRow)}
          onHide={this.onRenameHide}
          onSuccess={this.onRenameSubmit}
        />
        <Confirm
          ref={ref => {
            this.refDelete = ref;
          }}
        />
      </div>
    );
  }
}

TagManagerPage.propTypes = {
  tagManager: PropTypes.object.isRequired,
  getAllTags: PropTypes.func.isRequired,
  getTagConfig: PropTypes.func.isRequired,
  setTagConfigEnabled: PropTypes.func.isRequired,
  createTag: PropTypes.func.isRequired,
  updateTag: PropTypes.func.isRequired,
  deleteTag: PropTypes.func.isRequired,
};

/**
 * Maps the Tag Manager Redux slice to the page props.
 *
 * @param {Object} state - The root Redux state.
 * @param {TagManagerState} state.tagManager - The Tag Manager slice.
 * @returns {TagManagerPageProps} The mapped props.
 */
const mapStateToProps = state => ({
  tagManager: state.tagManager,
});
const mapDispatchToProps = {
  getAllTags,
  getTagConfig,
  setTagConfigEnabled,
  createTag,
  updateTag,
  deleteTag,
};

export default connect(mapStateToProps, mapDispatchToProps)(TagManagerPage);
